import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import * as Dialog from '@/components/ui/dialog';
import * as Select from '@/components/ui/select';
import { RoleGuard } from '@/components/role-guard';

export const Route = createFileRoute('/_app/admin/users')({ component: UsersPage });

const ROLES = ['admin', 'owner', 'manager', 'salesman', 'accountant'] as const;
type Role = typeof ROLES[number];

const NewUser = z.object({
  username: z.string().min(3).regex(/^[a-zA-Z0-9_.-]+$/),
  password: z.string().min(8),
  role: z.enum(ROLES),
  salesmanId: z.preprocess((v) => (v === '' || v == null ? null : Number(v)), z.number().int().nullable()),
  storeCodes: z.array(z.string()).default([]),
});
type NewUserForm = z.infer<typeof NewUser>;

interface StoreOption { store: string; label: string; default?: boolean }

interface UserRow {
  id: string; username: string; role: Role;
  salesmanId: number | null; disabled: boolean; storeCodes: string[];
}

function UsersPage() {
  return (
    <RoleGuard allow={['admin']} fallback={<p>Forbidden.</p>}>
      <UsersInner />
    </RoleGuard>
  );
}

async function fetchJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, { credentials: 'include', ...(init ?? {}) });
  if (!r.ok) {
    let msg = `Request failed (${r.status})`;
    try {
      const body = await r.json() as { error?: { message?: string } };
      if (body?.error?.message) msg = body.error.message;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

function UsersInner() {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<UserRow | null>(null);

  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => fetchJson<{ users: UserRow[] }>('/api/users').then((d) => d.users),
  });
  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: () => fetchJson<{ stores: StoreOption[] }>('/api/stores').then((d) => d.stores),
    staleTime: 5 * 60_000,
  });

  const createM = useMutation({
    mutationFn: async (v: NewUserForm) => {
      await fetchJson('/api/users', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          username: v.username, password: v.password, role: v.role,
          salesmanId: v.salesmanId, storeCodes: v.storeCodes,
        }),
      });
    },
    onSuccess: () => { setOpen(false); qc.invalidateQueries({ queryKey: ['users'] }); },
  });

  const toggleDisabled = useMutation({
    mutationFn: async (vars: { id: string; disabled: boolean }) => {
      await fetchJson(`/api/users/${vars.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ disabled: vars.disabled }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const updateStoresM = useMutation({
    mutationFn: async (vars: { id: string; storeCodes: string[] }) => {
      await fetchJson(`/api/users/${vars.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ storeCodes: vars.storeCodes }),
      });
    },
    onSuccess: () => { setEditingUser(null); qc.invalidateQueries({ queryKey: ['users'] }); },
  });

  const f = useForm<NewUserForm>({
    resolver: zodResolver(NewUser),
    defaultValues: { username: '', password: '', role: 'manager', salesmanId: null, storeCodes: [] },
  });

  const role = f.watch('role');
  const storeCodes = f.watch('storeCodes');
  const isPrivileged = role === 'admin' || role === 'owner';
  const needsStores = !isPrivileged && storeCodes.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Users</h1>
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger asChild><Button>New user</Button></Dialog.Trigger>
          <Dialog.Content>
            <Dialog.Title>Create user</Dialog.Title>
            <form className="mt-4 space-y-3" onSubmit={f.handleSubmit((v) => createM.mutate(v))}>
              <div className="space-y-1"><Label>Username</Label><Input {...f.register('username')} /></div>
              <div className="space-y-1"><Label>Password (min 8)</Label><Input type="password" {...f.register('password')} /></div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select.Root value={f.watch('role')} onValueChange={(v) => f.setValue('role', v as Role)}>
                  <Select.Trigger><Select.Value /></Select.Trigger>
                  <Select.Content>{ROLES.map((r) => <Select.Item key={r} value={r}>{r}</Select.Item>)}</Select.Content>
                </Select.Root>
              </div>
              <div className="space-y-1"><Label>Salesman ID (optional)</Label><Input {...f.register('salesmanId')} /></div>
              <div className="space-y-1.5">
                <Label>Stores {isPrivileged && <span className="text-xs text-muted-foreground">(admin/owner sees all)</span>}</Label>
                {storesQ.isLoading && <p className="text-xs text-muted-foreground">Loading stores…</p>}
                {storesQ.data && (
                  <div className="space-y-1.5 rounded-md border p-2">
                    {storesQ.data.map((s) => {
                      const checked = storeCodes.includes(s.store);
                      return (
                        <label key={s.store} className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isPrivileged}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...storeCodes, s.store]
                                : storeCodes.filter((c) => c !== s.store);
                              f.setValue('storeCodes', next, { shouldValidate: true });
                            }}
                          />
                          <span>{s.label}</span>
                          <span className="font-mono text-xs text-muted-foreground">{s.store}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {needsStores && (
                  <p className="text-xs text-amber-700">
                    Pick at least one store — without it, this user will see "Forbidden" on every page.
                  </p>
                )}
              </div>
              {createM.error && <p className="text-sm text-destructive">{(createM.error as Error).message}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createM.isPending || needsStores}>
                  {createM.isPending ? 'Creating…' : 'Create'}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Root>
      </div>

      <Card>
        <CardHeader><CardTitle>All users</CardTitle></CardHeader>
        <CardContent>
          {usersQ.isLoading && <p>Loading…</p>}
          {usersQ.error && <p className="text-sm text-destructive">{(usersQ.error as Error).message}</p>}
          {usersQ.data && (
            <Table>
              <THead><TR>
                <TH>Username</TH><TH>Role</TH><TH>Stores</TH><TH>Disabled</TH><TH>Last login</TH><TH></TH>
              </TR></THead>
              <TBody>
                {usersQ.data.map((u) => {
                  const isPriv = u.role === 'admin' || u.role === 'owner';
                  const noStores = !isPriv && u.storeCodes.length === 0;
                  return (
                    <TR key={u.id}>
                      <TD>{u.username}</TD>
                      <TD>{u.role}</TD>
                      <TD>
                        {u.storeCodes.length > 0
                          ? u.storeCodes.join(', ')
                          : isPriv
                            ? <span className="text-muted-foreground">(all)</span>
                            : <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">no access — set stores</span>
                        }
                      </TD>
                      <TD>{u.disabled ? 'yes' : ''}</TD>
                      <TD>—</TD>
                      <TD>
                        <div className="flex gap-1.5">
                          {!isPriv && (
                            <Button
                              variant={noStores ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setEditingUser(u)}
                            >
                              Stores
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => toggleDisabled.mutate({ id: u.id, disabled: !u.disabled })}>
                            {u.disabled ? 'Enable' : 'Disable'}
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {editingUser && (
        <EditStoresDialog
          user={editingUser}
          stores={storesQ.data ?? []}
          onClose={() => setEditingUser(null)}
          onSave={(codes) => updateStoresM.mutate({ id: editingUser.id, storeCodes: codes })}
          saving={updateStoresM.isPending}
          error={updateStoresM.error as Error | null}
        />
      )}
    </div>
  );
}

function EditStoresDialog({
  user, stores, onClose, onSave, saving, error,
}: {
  user: UserRow;
  stores: StoreOption[];
  onClose: () => void;
  onSave: (storeCodes: string[]) => void;
  saving: boolean;
  error: Error | null;
}) {
  const [selected, setSelected] = React.useState<string[]>(user.storeCodes);
  return (
    <Dialog.Root open onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <Dialog.Content>
        <Dialog.Title>Stores for {user.username}</Dialog.Title>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick the stores this user can see. Without at least one, every page returns "Forbidden".
        </p>
        <div className="mt-3 space-y-1.5 rounded-md border p-2">
          {stores.length === 0 && <p className="text-xs text-muted-foreground">Loading stores…</p>}
          {stores.map((s) => {
            const checked = selected.includes(s.store);
            return (
              <label key={s.store} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    setSelected((prev) =>
                      e.target.checked ? [...prev, s.store] : prev.filter((c) => c !== s.store)
                    );
                  }}
                />
                <span>{s.label}</span>
                <span className="font-mono text-xs text-muted-foreground">{s.store}</span>
              </label>
            );
          })}
        </div>
        {error && <p className="mt-2 text-sm text-destructive">{error.message}</p>}
        {selected.length === 0 && (
          <p className="mt-2 text-xs text-amber-700">
            Saving with no stores will lock this user out of all pages.
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave(selected)} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
